import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, GraduationCap, BookOpen, Users, Star, Award, MessageCircle, Phone } from 'lucide-react';
import bgImage from '../assets/images/bg/bg.png';

// Import teacher images
import msaidImage from '../assets/images/teachers/msaid.png';
import teacherImage1 from '../assets/images/teachers/7ARppHOKkmpGC16h3xD48w5mofS0zhG8OoQ2s8eC.png';
import teacherImage2 from '../assets/images/teachers/DoIOo5jmkgSGDcZIZsSYocnaI4mNsugAEYa6dOcZ.jpg';
import teacherImage3 from '../assets/images/teachers/IMG-20250621-WA0046 (1).png';
import teacherImage4 from '../assets/images/teachers/rJsJLfyMFhCDQfEtycgKH1ia3To19WvMFgaRNPAM.jpg';

const SubjectTeachersPage: React.FC = () => {
  const { subject } = useParams<{ subject: string }>();
  const navigate = useNavigate();

  // Subject data mapping
  const subjects = {
    'chemistry': {
      name: 'كيمياء',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/nWh1HHvYdDmefr9wbcq91HNs2Tyr0w5pu5Xyl2le.png',
      color: 'blue',
      description: 'تعلم الكيمياء مع أفضل المدرسين المتخصصين'
    },
    'physics': {
      name: 'فيزياء',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/LA7Kwue8nT622BUSn7PVQikTstngI7DLvMCGAMJH.png',
      color: 'green',
      description: 'أتقن الفيزياء مع مدرسين خبراء'
    },
    'arabic': {
      name: 'لغة عربية',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/uKWntv2HtpxZurqzGyMPOZJrWWLxz6YfDMHZfuKd.png',
      color: 'purple',
      description: 'تعلم اللغة العربية بطريقة صحيحة'
    },
    'math': {
      name: 'الرياضيات',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/EeFFhkl3frMa6CvSoNdQ3ZgfOxdXtZ6Wk9aW8xRh.png',
      color: 'orange',
      description: 'حل جميع مسائل الرياضيات بسهولة'
    },
    'english': {
      name: 'اللغة الإنجليزية',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/muT9yJlfSKc4V0MbpQcYlFzqgVQ4VLeFJX1I4g1p.png',
      color: 'red',
      description: 'تعلم الإنجليزية من الصفر للإتقان'
    },
    'biology': {
      name: 'الأحياء',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/8YBkTIW7EV65GXyMYgGS50UlLMnFKWZ0PbebURd5.png',
      color: 'teal',
      description: 'فهم علم الأحياء بطريقة تفاعلية'
    },
    'geography': {
      name: 'جغرافيا',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/XgGbvds9sIpFZtUAnDhMyFGqWrjMSfcoydIi8LeT.png',
      color: 'indigo',
      description: 'استكشف العالم من خلال الجغرافيا'
    },
    'french': {
      name: 'اللغة الفرنسية',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/LDpcofR3l9GUhi31QHy7mdUbhQ1QrVL82wxXqpGg.png',
      color: 'pink',
      description: 'تعلم الفرنسية مع مدرسين متخصصين'
    },
    'german': {
      name: 'اللغة الألمانية',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/9dX4xXiXGKZ1TG1q2z2jSgsYnlignhYJ2jvePHM7.png',
      color: 'yellow',
      description: 'أتقن الألمانية مع أفضل المدرسين'
    },
    'italian': {
      name: 'اللغة الإيطالية',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/9dX4xXiXGKZ1TG1q2z2jSgsYnlignhYJ2jvePHM7.png',
      color: 'cyan',
      description: 'تعلم الإيطالية بطريقة ممتعة'
    }
  };

  // Teachers data with subject mapping
  const allTeachers = [
    { id: 1, name: 'أحمد علي', image: teacherImage4, speciality: 'اللغة الإنجليزية', subjects: ['english'], experience: '10+ سنوات', rating: 5.0, studentsCount: 500 },
    { id: 2, name: 'أحمد محمود', image: msaidImage, speciality: 'الفيزياء', subjects: ['physics'], experience: '8+ سنوات', rating: 4.9, studentsCount: 400 },
    { id: 3, name: 'طارق أحمد', image: teacherImage1, speciality: 'الكيمياء', subjects: ['chemistry'], experience: '7+ سنوات', rating: 4.8, studentsCount: 350 },
    { id: 4, name: 'علي حسن', image: teacherImage2, speciality: 'الرياضيات', subjects: ['math'], experience: '12+ سنوات', rating: 5.0, studentsCount: 600 },
    { id: 5, name: 'نور الدين', image: teacherImage3, speciality: 'الأحياء', subjects: ['biology'], experience: '6+ سنوات', rating: 4.7, studentsCount: 300 },
  ];

  const currentSubject = subjects[subject as keyof typeof subjects];
  const subjectTeachers = allTeachers.filter(teacher => 
    teacher.subjects.includes(subject as string)
  );

  const handleTeacherClick = (teacherId: number) => {
    navigate(`/teacher/${teacherId}`);
  };

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'from-blue-500 to-blue-600',
      green: 'from-green-500 to-green-600',
      purple: 'from-purple-500 to-purple-600',
      orange: 'from-orange-500 to-orange-600',
      red: 'from-red-500 to-red-600',
      teal: 'from-teal-500 to-teal-600',
      indigo: 'from-indigo-500 to-indigo-600',
      pink: 'from-pink-500 to-pink-600',
      yellow: 'from-yellow-500 to-yellow-600',
      cyan: 'from-cyan-500 to-cyan-600'
    };
    return colorMap[color as keyof typeof colorMap] || 'from-blue-500 to-blue-600';
  };

  if (!currentSubject) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">المادة غير موجودة</h1>
          <button 
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            العودة للرئيسية
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header Section */}
      <div 
        className="relative text-white py-20 overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/70 via-purple-900/70 to-indigo-900/70"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Back Button */}
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-all duration-200 mb-12 hover:translate-x-1"
          >
            <ArrowRight className="w-6 h-6" />
            <span className="font-medium">العودة</span>
          </button>

          {/* Subject Header */}
          <div className="text-center">
            <div className="inline-flex items-center gap-4 bg-white/10 px-6 py-4 rounded-2xl mb-8 backdrop-blur-sm">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <img 
                  src={currentSubject.icon} 
                  alt={currentSubject.name}
                  className="w-10 h-10"
                />
              </div>
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                  {currentSubject.name}
                </h1>
                <p className="text-lg text-blue-200 mt-2">{currentSubject.description}</p>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center justify-center gap-8 text-center">
              <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm border border-white/20">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <Users className="w-8 h-8 text-green-400" />
                  <span className="text-3xl font-bold text-white">{subjectTeachers.length}</span>
                </div>
                <p className="text-blue-200">مدرس متخصص</p>
              </div>
              
              <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm border border-white/20">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <Star className="w-8 h-8 text-yellow-400" />
                  <span className="text-3xl font-bold text-white">4.9</span>
                </div>
                <p className="text-blue-200">تقييم متوسط</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Teachers Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              مدرسين متخصصين في {currentSubject.name}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              اختر المدرس المناسب لك من بين أفضل المدرسين المتخصصين في {currentSubject.name}
            </p>
          </div>

          {/* Teachers Grid */}
          {subjectTeachers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {subjectTeachers.map((teacher) => (
                <div
                  key={teacher.id}
                  onClick={() => handleTeacherClick(teacher.id)}
                  className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 hover:border-blue-200 cursor-pointer overflow-hidden"
                >
                  {/* Background Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${getColorClasses(currentSubject.color)} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                  
                  {/* Content */}
                  <div className="relative z-10">
                    {/* Teacher Image */}
                    <div className="text-center mb-6">
                      <div className="w-32 h-32 mx-auto rounded-2xl overflow-hidden shadow-lg ring-4 ring-gray-100 group-hover:ring-blue-200 transition-all duration-300">
                        <img
                          src={teacher.image}
                          alt={teacher.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>

                    {/* Teacher Info */}
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                        الأستاذ {teacher.name}
                      </h3>
                      <p className="text-lg text-gray-600 mb-4">{teacher.speciality}</p>
                      
                      {/* Rating */}
                      <div className="flex items-center justify-center gap-2 mb-4">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`w-5 h-5 ${i < Math.floor(teacher.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                            />
                          ))}
                        </div>
                        <span className="text-gray-600 font-medium">{teacher.rating}</span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <Users className="w-5 h-5 text-green-500" />
                          <span className="text-2xl font-bold text-gray-900">{teacher.studentsCount}+</span>
                        </div>
                        <p className="text-sm text-gray-600">طالب ناجح</p>
                      </div>
                      
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <Award className="w-5 h-5 text-purple-500" />
                          <span className="text-2xl font-bold text-gray-900">{teacher.experience}</span>
                        </div>
                        <p className="text-sm text-gray-600">خبرة</p>
                      </div>
                    </div>

                    {/* Action Button */}
                    <div className={`w-full bg-gradient-to-r ${getColorClasses(currentSubject.color)} text-white font-bold py-3 px-6 rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 group-hover:from-opacity-90 group-hover:to-opacity-90`}>
                      <BookOpen className="w-5 h-5" />
                      <span>عرض الكورسات</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <GraduationCap className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">لا يوجد مدرسين متاحين</h3>
              <p className="text-gray-600 mb-8">عذراً، لا يوجد مدرسين متخصصين في هذه المادة حالياً</p>
              <button 
                onClick={() => navigate('/')}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                العودة للرئيسية
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubjectTeachersPage;
